/** @jsx jsx */
import { jsx, Button as ThemeUIButton, ThemeUIStyleObject } from "theme-ui";
import React from "react";
import { PrimitiveProps } from "@styles/types";
import { Box } from "@shared-components/Box/Box";
import HexagonIcon from "@svg-icons/hexagon.component.svg";
import HexagonOutlineIcon from "@svg-icons/hexagon-outline.component.svg";
import { Icon } from "@shared-components/Icon/Icon";
import { Link, LinkProps } from "@shared-components/Link/Link";
import { getThemeVariantStyles } from "@styles/utils/getThemeVariant";
import {
  buttonTextStyles,
  buttonSharedStyles,
  buttonHoverAnimation,
  buttonOutlineStyle,
  buttonShadowStyle,
  buttonShadowWhiteFocusStyle,
  buttonPillStyle,
  buttonRoundStyle,
  buttonRoundSmallStyle,
  buttonHexagonOutlineStyle,
  buttonHexagonStyle,
  buttonHexagonIconStyle,
  buttonHollowCircleStyles,
} from "./styles";

type ShapeKey =
  | "default"
  | "pill"
  | "round"
  | "roundSmall"
  | "hollowCircle"
  | "hexagon";

interface ButtonStyleProps {
  outline?: boolean;
  shadow?: boolean;
  underlined?: boolean;
  icon?: React.ReactNode;
  shape?: ShapeKey;
}

type ShapeStyles = {
  [key in ShapeKey]: any; // or the specific style type
};

const getShapeStyle = (shape: ShapeKey, outline: boolean) => {
  const shapeStyles: ShapeStyles = {
    default: outline
      ? { ...buttonHoverAnimation, ...buttonOutlineStyle }
      : { ...buttonHoverAnimation },
    pill: buttonPillStyle,
    round: buttonRoundStyle,
    roundSmall: buttonRoundSmallStyle,
    hollowCircle: buttonHollowCircleStyles,
    hexagon: outline ? buttonHexagonOutlineStyle : buttonHexagonStyle,
  };

  return shapeStyles[shape] ?? null;
};

const getShadowStyle = (shadow: boolean, variant: string) => {
  if (!shadow) return {};

  return {
    ...buttonShadowStyle,
    ...(variant === "white" ? buttonShadowWhiteFocusStyle : {}),
  };
};

const sxStyle = (
  outline = false,
  shadow = false,
  shape: ShapeKey = "default",
  variant = ""
) => {
  return {
    ...buttonSharedStyles,
    ...getShapeStyle(shape, outline),
    ...getShadowStyle(shadow, variant),
  };
};

const ButtonText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box as="span" sx={{ ...buttonTextStyles }}>
    {children}
  </Box>
);

const variantStyles = (variant?: string, outline?: boolean) => {
  if (variant) {
    if (variant === "white" && outline) return "whiteOutline";
    if (variant === "copper" && outline) return "copperOutline";
    if (variant === "green" && outline) return "greenOutline";
    if (variant === "red" && outline) return "redOutline";
    return variant;
  }
  return outline ? "copperOutline" : "copper";
};

export interface ButtonProps
  extends PrimitiveProps,
    React.ComponentPropsWithoutRef<"button">,
    ButtonStyleProps {}
const Button: React.FC<ButtonProps> = ({
  children,
  sx,
  variant,
  outline,
  shadow,
  shape,
  icon,
  ...props
}) => {
  const inner =
    typeof children === "string" ? (
      <ButtonText>{children}</ButtonText>
    ) : (
      children
    );

  return (
    <ThemeUIButton
      sx={{ ...sxStyle(outline, shadow, shape, variant), ...sx }}
      variant={variantStyles(variant, outline)}
      {...props}
    >
      {shape === "hexagon" && (
        <Icon
          sx={{
            ...buttonHexagonIconStyle,
            ...(!outline && { stroke: "transparent", fill: "white" }),
          }}
          svg={outline ? HexagonOutlineIcon : HexagonIcon}
        />
      )}
      {icon}
      {inner}
    </ThemeUIButton>
  );
};

Button.defaultProps = {
  outline: false,
  shadow: false,
  shape: "default",
};

export interface ButtonLinkProps extends LinkProps, ButtonStyleProps {}

const ButtonLink: React.FC<ButtonLinkProps> = ({
  children,
  sx,
  variant,
  outline,
  shadow,
  shape,
  ...props
}) => {
  const inner =
    typeof children === "string" ? (
      <ButtonText>{children}</ButtonText>
    ) : (
      children
    );

  const buttonVariantStyles = getThemeVariantStyles(
    "buttons",
    variantStyles(variant, outline)
  );
  const sxStyles = sxStyle(outline, shadow, shape, variant);
  const mergeButtonStyles: { [key: string]: ThemeUIStyleObject } = {
    ...buttonVariantStyles,
    ...sxStyles,
  };

  Object.keys(mergeButtonStyles).forEach((key) => {
    mergeButtonStyles[key] =
      typeof mergeButtonStyles[key] === "object"
        ? Object.assign(
            mergeButtonStyles[key],
            // @ts-ignore
            buttonVariantStyles[key],
            sxStyles[key]
          )
        : mergeButtonStyles[key];
  });

  return (
    <Link
      sx={{
        ...mergeButtonStyles,
        ...sx,
      }}
      {...props}
    >
      {shape === "hexagon" && (
        <Icon
          sx={{
            ...buttonHexagonIconStyle,
            ...(!outline && { stroke: "transparent", fill: "white" }),
          }}
          svg={outline ? HexagonOutlineIcon : HexagonIcon}
        />
      )}
      {inner}
    </Link>
  );
};

ButtonLink.defaultProps = {
  outline: false,
  shadow: false,
  shape: "default",
};

export { Button, ButtonLink };
